import { Bus } from 'core/infrastructure';
import {
	WhenPrint,
	ChangePrintModeRequest,
	ChangePrintModeResponse,
	GetDeliveryNotePreviewRequest,
	GetDeliveryNotePreviewResponse,
	GetInvoicePreviewRequest,
	GetInvoicePreviewResponse,
	GetPartialBalanceReportPreviewRequest,
	GetPartialBalanceReportPreviewResponse,
	GetSalesOrderPreviewRequest,
	GetSalesOrderPreviewResponse,
	GetTicketPreviewRequest,
	GetTicketPreviewResponse,
	GetTotalBalanceReportPreviewRequest,
	GetTotalBalanceReportPreviewResponse,
	MarkDeliveryNoteAsPrintedRequest,
	MarkInvoiceAsPrintedRequest,
	MarkSalesOrderAsPrintedRequest,
	PrintDeliveryNoteOnPrinterRequest,
	PrintDeliveryNotesOnPrinterRequest,
	PrintInvoiceOnPrinterRequest,
	PrintPartialBalanceReportRequest,
	PrintPreviewDTO,
	PrintSalesOrderOnPrinterRequest,
	PrintTemplateInListDTO,
	PrintTicketRequest,
	PrintTotalBalanceReportRequest,
	GetPreparationSummaryPreviewRequest,
	GetPreparationSummaryPreviewResponse,
	PrintPreparationSummaryRequest,
	SalesOrderDTO,
	GetPrintDocumentOptionsRequest,
	GetPrintInvoiceOptionsResponse,
	PrintDocumentOptionsDTO,
	DocumentType,
	PrintCouponsForDeliveryNoteRequest,
	PrintCouponsForInvoiceRequest,
	PrintInvoiceRequest,
	PrintCashControlStatusRequest,
	GetTrainingSessionReportPreviewRequest,
	GetTrainingSessionReportPreviewResponse,
	PrintTrainingSessionReportRequest,
	GetSystemCloseOutReportPreviewRequest,
	GetSystemCloseOutReportPreviewResponse,
	PrintSystemCloseOutReportRequest,
	PrintGiftInvoiceRequest
} from 'core/api';

const process = Bus.buildProcessFn('/bus/');

export async function changePrintMode(): Promise<WhenPrint> {
	const request = new ChangePrintModeRequest();
	return await process(request, (response: ChangePrintModeResponse) => response.WhenPrint);
}

export type PrintParams = {
	printer: string,
	printTemplate: PrintTemplateInListDTO,
	contentReplacement: string
};

export async function getInvoicePreviews(invoiceId: number): Promise<PrintPreviewDTO[]> {
	const request = new GetInvoicePreviewRequest({ invoiceId });
	return process(request, (response: GetInvoicePreviewResponse) => response.Previews);
}

export async function getDeliveryNotePreviews(deliveryNoteId: number): Promise<PrintPreviewDTO[]> {
	const request = new GetDeliveryNotePreviewRequest({ deliveryNoteId });
	return process(request, (response: GetDeliveryNotePreviewResponse) => response.Previews);
}

export async function getTicketPreviews(ticketId: number): Promise<PrintPreviewDTO[]> {
	const request = new GetTicketPreviewRequest({ ticketId });
	return process(request, (response: GetTicketPreviewResponse) => response.Previews);
}

export async function getSalesOrderPreview(orderId: number): Promise<PrintPreviewDTO[]> {
	const request = new GetSalesOrderPreviewRequest({ orderId });
	return process(request, (response: GetSalesOrderPreviewResponse) => response.Previews);
}

export async function markDeliveryNoteAsPrinted(deliveryNoteId: number): Promise<void> {
	const request = new MarkDeliveryNoteAsPrintedRequest({ deliveryNoteId });
	return process(request);
}

export async function markSalesOrderAsPrinted(salesOrderId: number): Promise<void> {
	const request = new MarkSalesOrderAsPrintedRequest({ salesOrderId });
	return process(request);
}

export async function markInvoiceAsPrinted(invoiceId: number): Promise<void> {
	const request = new MarkInvoiceAsPrintedRequest({ invoiceId });
	return process(request);
}

export async function printTicket(ticketIds: number[]): Promise<void> {
	const request = new PrintTicketRequest({ ticketIds });
	return process(request);
}

export async function printTotalBalance(): Promise<void> {
	const request = new PrintTotalBalanceReportRequest();
	return process(request);
}

export async function printPartialBalance(): Promise<void> {
	const request = new PrintPartialBalanceReportRequest();
	return process(request);
}

export async function getPartialBalancePreview(): Promise<PrintPreviewDTO> {
	const request = new GetPartialBalanceReportPreviewRequest();
	return process(request, (response: GetPartialBalanceReportPreviewResponse) => response.Preview);
}

export async function getTotalBalancePreview(): Promise<PrintPreviewDTO> {
	const request = new GetTotalBalanceReportPreviewRequest();
	return process(request, (response: GetTotalBalanceReportPreviewResponse) => response.Preview);
}

export async function printInvoiceOnPrinter(invoiceId: number, params: PrintParams): Promise<void> {
	const request = new PrintInvoiceOnPrinterRequest({ invoiceId, printer: params.printer, templateId: params.printTemplate.Id, contentReplacement: params.contentReplacement });
	await process(request);
}

export async function printSalesOrderOnPrinter(salesOrderId: number, params: PrintParams): Promise<void> {
	const request = new PrintSalesOrderOnPrinterRequest({ salesOrderId, printer: params.printer, templateId: params.printTemplate.Id, contentReplacement: params.contentReplacement });
	await process(request);
}

export async function printDeliveryNoteOnPrinter(deliveryNoteId: number, params: PrintParams): Promise<void> {
	const request = new PrintDeliveryNoteOnPrinterRequest({ deliveryNoteId, printer: params.printer, templateId: params.printTemplate.Id, contentReplacement: params.contentReplacement });
	await process(request);
}

export async function printDeliveryNotesOnPrinter(deliveryNoteIds: number[], params: PrintParams): Promise<void> {
	const request = new PrintDeliveryNotesOnPrinterRequest({ deliveryNoteIds, printer: params.printer, templateId: params.printTemplate.Id, contentReplacement: params.contentReplacement });
	await process(request);
}

export function getSalesOrderPreviews(items: SalesOrderDTO[]): Promise<PrintPreviewDTO> {
	const request = new GetPreparationSummaryPreviewRequest({ ticketIds: items.map(orders => orders.TicketId) });
	return process(request, (response: GetPreparationSummaryPreviewResponse) => response.Preview);
}

export async function printPreparationSummary(ticketIds: number[]): Promise<void> {
	const request = new PrintPreparationSummaryRequest({ ticketIds });
	await process(request);
}

export async function printInvoiceCoupons(invoiceId: number): Promise<void> {
	const request = new PrintCouponsForInvoiceRequest({ invoiceId });
	await process(request);
}
export async function getPrintOptions(documentType: DocumentType): Promise<PrintDocumentOptionsDTO> {
	const request = new GetPrintDocumentOptionsRequest({ documentType });
	return await process(request, (response: GetPrintInvoiceOptionsResponse) => response.Options);
}

export async function printDeliveryNoteCoupons(deliveryNoteId: number): Promise<void> {
	const request = new PrintCouponsForDeliveryNoteRequest({ deliveryNoteId });
	return await process(request);
}

export async function printInvoice(invoiceId: number): Promise<void> {
	const request = new PrintInvoiceRequest({ invoiceId });
	return process(request);
}

export async function printCashControlStatus(coinsValues: number, notesValues: number, remainingChange: number): Promise<void> {
	const request = new PrintCashControlStatusRequest({
		status: {
			CoinsValues: coinsValues,
			NotesValues: notesValues,
			RemainingChange: remainingChange,
			TotalValues: coinsValues + notesValues
		}
	});
	return process(request);
}

export async function generateTrainingPreview(): Promise<string> {
	const request = new GetTrainingSessionReportPreviewRequest();
	return process(request, (response: GetTrainingSessionReportPreviewResponse) => response.Preview);
}

export async function printTraining(): Promise<void> {
	const request = new PrintTrainingSessionReportRequest({ summaryId: 0 });
	return process(request);
}

export async function getSystemCloseOutReportPreview(): Promise<string> {
	const request = new GetSystemCloseOutReportPreviewRequest();
	return process(request, (response: GetSystemCloseOutReportPreviewResponse) => response.Preview);
}

export async function printSystemCloseOut(): Promise<void> {
	const request = new PrintSystemCloseOutReportRequest();
	return process(request);
}

export function printInvoiceGiftLines(invoiceId: number, lineIds: number[]): Promise<void> {
	const request = new PrintGiftInvoiceRequest({ invoiceId, lineIdsIncluded: lineIds, includeAll: false });
	return process(request);
}

export function printInvoiceGift(invoiceId: number): Promise<void> {
	const request = new PrintGiftInvoiceRequest({ invoiceId, includeAll: true, lineIdsIncluded: [] });
	return process(request);
}
